// Shared helpers for anything that renders parsed NDH/NPD data
// (src/utils/ndhLookup.js output) — kept in one place so the modal
// version and the inline tab version can't quietly drift apart.

export function getInitials(name) {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

export function capitalize(word) {
  if (!word) return "";
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function formatDate(isoDateStr) {
  if (!isoDateStr) return null;
  const d = new Date(isoDateStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export function yearsSince(isoDateStr) {
  if (!isoDateStr) return null;
  const d = new Date(isoDateStr);
  if (isNaN(d.getTime())) return null;
  const years = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  return Math.floor(years);
}

export function formatPhone(raw) {
  if (!raw) return raw;
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

export function telHref(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  return `tel:+${digits.length === 10 ? "1" + digits : digits}`;
}

// Pulls a short credential abbreviation (e.g. "MD", "DO") out of a
// longer display string like "Medical Doctor (MD)". Returns null
// rather than guessing if the credential string doesn't contain one.
export function extractCredentialBadge(credential) {
  if (!credential) return null;
  const parenMatch = credential.match(/\(([A-Z]{2,6})\)/);
  if (parenMatch) return parenMatch[1];
  if (/^[A-Z]{2,6}$/.test(credential.trim())) return credential.trim();
  return null;
}

// Groups a practitioner's practices by physical address, so a doctor
// billed under several affiliated NPIs at one hub location renders as
// one consolidated card with a chip per entity. Splits entities into
// active/inactive (using the real PractitionerRole.active flag) so a
// lapsed affiliation shows up honestly instead of being silently
// dropped or silently kept.
export function buildLocationHub(practices) {
  const groups = new Map();
  (practices || []).forEach((practice) => {
    (practice.locations || []).forEach((loc) => {
      const key = [loc.line, loc.city, loc.state, loc.postalCode].filter(Boolean).join("|");
      if (!key) return;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          line: loc.line,
          city: loc.city,
          state: loc.state,
          postalCode: loc.postalCode,
          phone: loc.phone,
          fax: loc.fax,
          latitude: loc.latitude,
          longitude: loc.longitude,
          entities: [],
        });
      }
      const group = groups.get(key);
      if (group.latitude == null && loc.latitude != null) {
        group.latitude = loc.latitude;
        group.longitude = loc.longitude;
      }
      if (!group.phone && loc.phone) group.phone = loc.phone;
      if (!group.fax && loc.fax) group.fax = loc.fax;
      group.entities.push({
        id: practice.id,
        name: practice.organizationName,
        npi: practice.organizationNpi,
        type: practice.organizationType || null,
        active: practice.active !== false,
      });
    });
  });
  return Array.from(groups.values()).sort((a, b) => b.entities.length - a.entities.length);
}

// Set REACT_APP_GOOGLE_MAPS_API_KEY in your .env to use real Google Maps
// embeds. Falls back to an OpenStreetMap embed (no key needed) if it's
// not set, so the map never just breaks — it degrades gracefully.
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export function buildMapEmbedUrl(lat, lng) {
  if (GOOGLE_MAPS_API_KEY) {
    return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${lat},${lng}&zoom=15`;
  }
  const delta = 0.01;
  const bbox = [lng - delta, lat - delta, lng + delta, lat + delta].join("%2C");
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lng}`;
}

export function buildDirectionsUrl(lat, lng) {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

// true -> good/confirmed. false -> a definite, reported "no". null/undefined
// -> genuinely not reported, shown as pending rather than a false negative.
export function flagStatus(value, trueText, falseText, pendingText) {
  if (value === true) return { tone: "good", text: trueText };
  if (value === false) return { tone: "neutral", text: falseText };
  return { tone: "pending", text: pendingText };
}